/**
 * HR help text for the Attendance module.
 *
 * How attendance actually works: punches arrive as attendance logs (from the
 * machine, an import, the portal, or an approved attendance request). A
 * background calculation pairs those logs with the employee's rostered shift and
 * writes one attendance day, deciding present, absent, late, early, short
 * duration, and overtime. Payroll then reads those days through the attendance
 * policies. So a wrong punch, or a day never recalculated, changes salary.
 */

const attendanceLog = {
  employee_id: "Employee whose punch this is. It must be the employee code the attendance machine uses, otherwise the punch is never matched to a shift.",
  punch_type: "What this punch represents: check in, check out, break out, break in, or overtime. The calculation uses this to decide whether the time counts as arrival, departure, or a break.",
  punch_date: "The date the punch happened. A punch entered on the wrong date leaves the correct day looking absent or missing.",
  punch_time: "The exact time of the punch. This is compared with the shift timing to decide late arrival, early leaving, and worked time.",
  description: "Reason for adding this punch by hand, for example a machine failure. Kept for audit.",
};

const importLogs = {
  file_attachment: "Excel file of punches to import. Use the sample file so the employee code, punch type, and punch time columns match; rows that do not match an employee code are not imported.",
  date: "Day the sample file should be prepared for. The download contains that day's expected rows for you to fill in.",
  start: "Pull punches recorded from this date onwards from the selected machines. Keep the range short; a long range pulls a large number of punches.",
  machines: "Attendance machines to pull punches from. Only ticked machines are contacted.",
};

const attendanceRequest = {
  employment_id: "Employee this attendance correction is for. Their approval workflow decides who receives the request.",
  date: "The day being corrected. Changing it reloads the shift timing shown below.",
  expected_check_in: "The shift's scheduled start time for the selected day. Shown for reference and cannot be edited here; it comes from the roster.",
  expected_check_out: "The shift's scheduled end time for the selected day. Shown for reference and cannot be edited here; it comes from the roster.",
  punch_type: "Whether the missing punch is a check in or a check out. The wrong choice corrects the wrong side of the day.",
  punch_time: "The time that should have been punched. Once the request is fully approved, the system adds this as a real punch and recalculates the day, which can clear an absent or late mark.",
  description: "Explain why the punch is missing. Approvers read this before approving.",
  last_remarks: "Remarks written by the last approver. Visible in the request history.",
  "requestor.full_name_code": "The employee who raised this request, with their employee code.",
  approved_time: "The punch time as approved. This is what is written into attendance, not necessarily what was requested.",
};

const timesheet = {
  date: "The attendance day being corrected. It cannot be changed here; close and pick the correct day from the timesheet.",
  marked_absent: "Force this day to Absent. It clears the present, missing, late, early, short duration, and overtime marks, and stops recalculation from changing the day again. Payroll will then deduct through the absent policy.",
  checkin_expected_time: "The shift's scheduled start time, taken from the roster. Read-only; change the roster if this is wrong.",
  checkin_punch_time: "The actual check-in time for this day. Editing it recalculates late arrival and worked time, which changes what payroll deducts.",
  checkout_expected_time: "The shift's scheduled end time, taken from the roster. Read-only; change the roster if this is wrong.",
  checkout_punch_time: "The actual check-out time for this day. Editing it recalculates early leaving, short duration, and overtime.",
  is_manually_updated: "Set automatically once HR edits this day. From then on the system keeps your times and stops replacing them with machine punches, so a later machine sync will not undo this correction.",
  reason: "Why this attendance day was corrected by hand. Kept with the record for audit.",
};

const calculate = {
  start: "First day to recalculate. Attendance for these days is rebuilt from the punches currently in the system.",
  end: "Last day to recalculate. Keep the range to the days that actually need fixing, since each day is rebuilt in the background.",
  all: "Recalculate every employee. This also recalculates overtime, and on a large company it is a heavy background job, so prefer selecting a group.",
  type: "Choose whether to recalculate by branch, by department, or by picking employees.",
  employment_ids: "Employees to recalculate. Their attendance days in the selected range are rebuilt from their punches.",
  department_ids: "Departments to recalculate. Employees in the departments below the selected ones are included as well.",
  branch_ids: "Branches to recalculate. Every employee of the selected branches is included.",
};

const overtimeRequest = {
  employment_id: "Employee who worked the overtime. Their approval workflow decides who receives the request.",
  date: "The day the overtime was worked. It must be a day the employee had a shift, otherwise the overtime cannot be matched.",
  start: "Time the overtime started. Together with the end time this is the requested duration.",
  end: "Time the overtime ended. The requested duration is what approvers see, but only the approved duration is paid.",
  description: "Explain what work was done. Approvers read this before approving.",
  approved_duration: "Minutes actually approved for payment. The approver can reduce it below what was requested, and payroll pays only this number through the overtime policy.",
  last_remarks: "Remarks written by the last approver. Visible in the request history.",
};

const breakType = {
  name: "Name of the break, such as Lunch or Prayer. It appears against the break rows on the attendance day.",
  start: "The machine punch code that opens this break, for example Break Out. It must match exactly what the attendance machine sends, otherwise breaks are never detected.",
  end: "The machine punch code that closes this break, for example Break In. Punches between the opening and closing code become the break time deducted from worked hours.",
};

export const attendanceTooltips = {
  attendanceLog,
  importLogs,
  attendanceRequest,
  timesheet,
  calculate,
  overtimeRequest,
  breakType,
};

export default attendanceTooltips;
